// Centralized dropdown options matching database constraints exactly
// These values must match the CHECK constraints in the database tables

// Investor types - matches investors_investor_type_check constraint
export const INVESTOR_TYPES = [
  "Business Angel",
  "VC",
  "Growth Equity",
  "PE/Buy-Out",
  "Family Office",
  "Corporate VC",
  "Government-backed",
  "Incubator, Accelerator",
  "Startup Studio",
  "Public Fund",
  "Revenue-based"
] as const;

// Investment types - matches investors investment_types array values
export const INVESTMENT_TYPES = [
  "Majority",
  "Minority",
  "Private Debt",
  "Venture Debt"
] as const;

// Focus areas - shared by investors, startups, and advisors
export const FOCUS_AREAS = [
  "AI",
  "Cloud/SaaS",
  "Climate",
  "Consumer",
  "Fintech",
  "Media",
  "Hardware",
  "Security",
  "Healthcare",
  "Services"
] as const;

// Funding rounds - matches investors funding_rounds array values
export const FUNDING_ROUNDS = [
  "Pre-Seed",
  "Seed",
  "Series A",
  "Series B",
  "Series C",
  "Series D",
  "Growth Capital",
  "PE/Buy-out",
  "Secondaries"
] as const;

// Fundraising round types - subset of FUNDING_ROUNDS for startup fundraising processes
// Excludes investor-only types (PE/Buy-out, Secondaries)
export const FUNDRAISING_ROUND_TYPES = FUNDING_ROUNDS.filter(
  r => r !== "PE/Buy-out" && r !== "Secondaries"
);

// Startup funding stages - subset of funding rounds for startup profiles
export const STARTUP_FUNDING_STAGES = [
  "Pre-Seed",
  "Seed",
  "Series A",
  "Series B",
  "Series C",
  "Series D",
  "Growth Capital"
] as const;

// Liquidation preference types for valuation benchmarking
export const LIQUIDATION_PREFERENCE_TYPES = [
  "non-participating",
  "participating"
] as const;

export const LIQUIDATION_MULTIPLES = [
  "1x",
  "2x", 
  "3x",
  "4x",
  "5x"
] as const;

// Lead Investor options for filter and form dropdowns
export const LEAD_INVESTOR_OPTIONS = [
  { value: "all", label: "All investors" },
  { value: "true", label: "Leads rounds" },
  { value: "false", label: "Does not lead" }
] as const;

// ARR size ranges for benchmarking (shared across KPI and valuation benchmarks)
export const ARR_SIZE_RANGES = [
  { key: '0-1M', label: '$0-1M', min: 0, max: 1 },
  { key: '1-10M', label: '$1-10M', min: 1, max: 10 },
  { key: '10-50M', label: '$10-50M', min: 10, max: 50 },
  { key: '50M+', label: '$50M+', min: 50, max: null },
] as const;

// Benchmark filter countries (curated list for benchmark filters)
export const BENCHMARK_COUNTRIES = [
  'United States', 'United Kingdom', 'Germany', 'France', 'Canada', 
  'Netherlands', 'Switzerland', 'Austria', 'Sweden', 'Norway', 
  'Denmark', 'Finland', 'Spain', 'Italy', 'Belgium', 'Ireland', 
  'Australia', 'New Zealand', 'Singapore', 'Japan', 'South Korea', 
  'India', 'Brazil', 'Mexico', 'Israel'
] as const;

// Regions mapping (extracted from InvestorFilters for reuse)
export const REGIONS_WITH_COUNTRIES: Record<string, string[]> = {
  "North America": ["United States", "Canada", "Mexico"],
  "Western Europe": ["United Kingdom", "Germany", "France", "Netherlands", "Switzerland", "Belgium", "Austria", "Ireland", "Luxembourg", "Monaco", "Liechtenstein", "Andorra", "San Marino", "Vatican City"],
  "Northern Europe": ["Sweden", "Norway", "Denmark", "Finland", "Iceland"],
  "Southern Europe": ["Spain", "Italy", "Portugal", "Greece", "Malta", "Cyprus"],
  "Eastern Europe": ["Poland", "Czech Republic", "Hungary", "Romania", "Bulgaria", "Slovakia", "Croatia", "Slovenia", "Estonia", "Latvia", "Lithuania", "Kosovo", "Albania", "Belarus", "Bosnia and Herzegovina", "Moldova", "Montenegro", "North Macedonia", "Serbia", "Ukraine"],
  "Middle East": ["United Arab Emirates", "Israel", "Saudi Arabia", "Qatar", "Kuwait", "Bahrain", "Oman", "Jordan", "Lebanon", "Turkey", "Iran", "Iraq", "Syria", "Yemen"],
  "Asia-Pacific": ["Singapore", "Japan", "South Korea", "Australia", "New Zealand", "Hong Kong", "Taiwan", "Fiji", "Kiribati", "Marshall Islands", "Micronesia", "Nauru", "Palau", "Papua New Guinea", "Samoa", "Solomon Islands", "Tonga", "Tuvalu", "Vanuatu"],
  "South Asia": ["India", "Pakistan", "Bangladesh", "Sri Lanka", "Afghanistan", "Bhutan", "Maldives", "Nepal"],
  "Southeast Asia": ["Indonesia", "Malaysia", "Thailand", "Philippines", "Vietnam", "Brunei", "Cambodia", "Laos", "Myanmar", "Timor-Leste"],
  "East Asia": ["China", "Japan", "South Korea", "Mongolia", "North Korea", "Taiwan"],
  "Latin America": ["Brazil", "Argentina", "Chile", "Colombia", "Peru", "Uruguay", "Bolivia", "Ecuador", "Guyana", "Paraguay", "Suriname", "Venezuela"],
  "Central America & Caribbean": ["Costa Rica", "Panama", "Guatemala", "Honduras", "Nicaragua", "El Salvador", "Belize", "Jamaica", "Trinidad and Tobago", "Bahamas", "Barbados", "Dominican Republic", "Haiti", "Cuba", "Antigua and Barbuda", "Dominica", "Grenada", "Saint Lucia", "Saint Vincent and the Grenadines"],
  "Africa": ["South Africa", "Nigeria", "Kenya", "Egypt", "Ghana", "Morocco", "Algeria", "Angola", "Benin", "Botswana", "Burkina Faso", "Burundi", "Cabo Verde", "Cameroon", "Central African Republic", "Chad", "Comoros", "Congo", "Djibouti", "Equatorial Guinea", "Eritrea", "Eswatini", "Ethiopia", "Gabon", "Gambia", "Guinea", "Guinea-Bissau", "Lesotho", "Liberia", "Libya", "Madagascar", "Malawi", "Mali", "Mauritania", "Mauritius", "Mozambique", "Namibia", "Niger", "Rwanda", "Sao Tome and Principe", "Senegal", "Seychelles", "Sierra Leone", "Somalia", "South Sudan", "Sudan", "Tanzania", "Togo", "Tunisia", "Uganda", "Zambia", "Zimbabwe"],
  "Russia & Central Asia": ["Russia", "Kazakhstan", "Uzbekistan", "Kyrgyzstan", "Tajikistan", "Turkmenistan", "Azerbaijan", "Armenia", "Georgia"]
};

export const REGIONS = Object.keys(REGIONS_WITH_COUNTRIES) as readonly string[];

// Re-export advisor options from the existing file for centralized access
export { ADVISOR_TYPES, ADVISOR_SKILLS, INDUSTRY_EXPERIENCE } from './advisorOptions';

// TypeScript types derived from the constants
export type InvestorType = typeof INVESTOR_TYPES[number];
export type InvestmentType = typeof INVESTMENT_TYPES[number];
export type FocusArea = typeof FOCUS_AREAS[number];
export type FundingRound = typeof FUNDING_ROUNDS[number];
export type StartupFundingStage = typeof STARTUP_FUNDING_STAGES[number];
